import React, { ComponentProps } from 'react';
import { styled, Button, darken, Theme } from '@material-ui/core';
import { PaletteColor } from '@material-ui/core/styles/createPalette';
import { useSelector } from 'react-redux';

import { useGridButtonSize } from 'components/GridButton';
import { getGridIsOpened } from 'reducers/UI/gridDisplay';

type ButtonColor =
  | keyof Pick<Theme['palette'], 'primary' | 'secondary'>
  | 'grid_green'
  | 'grid_red'
  | 'grid_yellow'
  | 'grid_light'
  | 'grid_yellow'
  | 'grid_dark'
  | 'POS'
  | 'accent'
  | 'error'
  | 'light'
  | 'success'
  | 'warning'
  | 'info';

function isPaletteColor(
  color: Theme['palette'][keyof Theme['palette']],
): color is PaletteColor {
  return typeof color === 'object' && 'main' in color;
}

const StyledButton = styled(Button)<
  Theme,
  {
    width: number | string;
    height?: number;
    buttonColor: ButtonColor;
    removeMargin?: boolean;
  }
>(({ theme, height, width, buttonColor, removeMargin = false }) => {
  const paletteColor = theme.palette[buttonColor];
  const color = isPaletteColor(paletteColor) ? paletteColor.main : '';
  const marginStyle = removeMargin ? {} : { margin: '0px 4px 8px' };
  return {
    ...marginStyle,
    height: `${height ?? 95}px`,
    width,
    background: color,
    color: '#212529',
    boxShadow: 'none',
    fontWeight: 400,
    padding: '2px 4px',

    '&:hover': {
      background: darken(color, 0.2),
    },
  };
});

/**
 * Get the full width of a button, given how many columns it should span
 * @param colspan How many columns to span
 * @param columnWidth How wide is a single button (95px in retail view, but varies in grid view)
 */
function getButtonWidth(colspan: number | string, columnWidth: number) {
  if (typeof colspan === 'string') return colspan;

  // columns: ↓↓↓↓↓  ↓↓↓↓↓  ↓↓↓↓↓
  // gap:          ↓↓     ↓↓
  //          [btn]  [btn]  [btn]
  //          [btn]  [btn]  [btn]
  const columns = colspan * columnWidth;
  const gap = 8 * (colspan - 1);

  return `${columns + gap}px`;
}

function ActionButton({ width, ...rest }: ComponentProps<typeof StyledButton>) {
  const { width: gridButtonWidth } = useGridButtonSize();
  const isGridMode = useSelector(getGridIsOpened);

  const columnWidth = isGridMode ? gridButtonWidth : 95;
  return <StyledButton {...rest} width={getButtonWidth(width, columnWidth)} />;
}

export default ActionButton;
